import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

const restrictionCache = new CacheManager('VueCache', 'cache', {
    maxAge: 24 * 60 * 60 * 1000,
    memoryCacheTTL: 30 * 60 * 1000,
    logCategory: 'Restriction',
    keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.restrictions.`,
});

export async function getProductTreeRestrictions(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = restrictionCache.getFromMemory('productTreeRestrictions');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('PRODUCTTREERESTRICTIONS');
            if (legacyData) {
                log('info', 'Restriction', '📦 Seeding Vue cache from legacy storage (PRODUCTTREERESTRICTIONS)');
                await restrictionCache.put('productTreeRestrictions', legacyData);
                restrictionCache.setInMemory('productTreeRestrictions', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'Restriction', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return restrictionCache.fetchOrCache('productTreeRestrictions', fetchProductTreeRestrictionsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getProductTreeCrossRestrictions(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = restrictionCache.getFromMemory('productTreeCrossRestrictions');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('PT_CROSS_RESTRICTIONS');
            if (legacyData) {
                log('info', 'Restriction', '📦 Seeding Vue cache from legacy storage (PT_CROSS_RESTRICTIONS)');
                await restrictionCache.put('productTreeCrossRestrictions', legacyData);
                restrictionCache.setInMemory('productTreeCrossRestrictions', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'Restriction', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return restrictionCache.fetchOrCache('productTreeCrossRestrictions', fetchProductTreeCrossRestrictionsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

async function fetchProductTreeRestrictionsFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Core/ProductTreeRestrictions'), { method: 'GET' }, 'token');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const restrictions = await response.json();
    log('success', 'Restriction', `✅ Fetched ${restrictions.length} product tree restrictions`);
    return restrictions;
}

async function fetchProductTreeCrossRestrictionsFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Core/ProductTreeCrossRestrictions'), { method: 'GET' }, 'token');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const crossRestrictions = await response.json();
    log('success', 'Restriction', `✅ Fetched ${crossRestrictions.length} product tree cross restrictions`);
    return crossRestrictions;
}

export async function refreshRestrictions() {
    restrictionCache.clearMemory();
    await restrictionCache.clear();
}
